/**
 * Shared client-side error reporter — sends real, captured errors (React
 * crashes, uncaught exceptions, unhandled promise rejections, failed API
 * calls) to the backend's public `log_client_error` action, which writes
 * them into `system_error_logs` (visible in admin's Live Error Log).
 *
 * Never throws — a reporting failure must never cascade into the app.
 * De-dupes identical (type+message) reports within a session so a render
 * loop or repeated failed request can't flood the log.
 */
import { BRIDGE_API_URL } from './serverConfig';

export type ClientErrorType = 'react_error' | 'window_error' | 'unhandled_rejection' | 'network_error' | 'api_error';

interface ReportClientErrorInput {
  type: ClientErrorType;
  message: string;
  stack?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

const reportedThisSession = new Set<string>();
const MAX_REPORTS_PER_SESSION = 100;

export function reportClientError({ type, message, stack, severity = 'medium' }: ReportClientErrorInput): void {
  try {
    if (!message) return;
    const dedupeKey = `${type}:${message}`.slice(0, 300);
    if (reportedThisSession.has(dedupeKey)) return;
    if (reportedThisSession.size >= MAX_REPORTS_PER_SESSION) return;
    reportedThisSession.add(dedupeKey);

    const body = JSON.stringify({
      type,
      message: message.slice(0, 2000),
      stack: stack ? stack.slice(0, 5000) : undefined,
      url: window.location.href,
      severity,
    });

    // `keepalive` lets this survive a page unload (e.g. error right before navigation).
    // Raw fetch (not apiFetch/bridgeApi) — those live in apiError.ts, which itself
    // reports network errors here, so going through them would be circular.
    fetch(`${BRIDGE_API_URL}?action=log_client_error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow — reporting must never throw */ });
  } catch {
    // swallow
  }
}
